import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  Typography,
  Row,
  Col,
  Card,
  Space,
  Modal,
  Spin,
  Alert,
  Descriptions,
  Empty,
} from "antd";
import {
  MapPin,
  XCircle,
  ChevronRight,
  Mail,
  Phone,
  User,
  Calendar,
  DollarSign,
  ShoppingBag,
  Award,
} from "lucide-react";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { useNavigate } from "react-router-dom";
import { mergeDecrypted } from "../../utils/decrypt";

const { Title, Text } = Typography;

const CustomerDetails = ({ customer, onBack }) => {
  const [open, setOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [userOrders, setUserOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const { request } = useRequest();

  useEffect(() => {
    fetchUserDetails();
  }, [customer]);

  const fetchUserDetails = () => {
    setLoading(true);
    setErrorMessage("");

    // Get user ID from customer object
    const userId = customer.id || customer.source_user_id;

    if (!userId) {
      setErrorMessage("Invalid user ID");
      setLoading(false);
      return;
    }

    request({
      url: apiPath.userOrderDetails(userId),
      method: "GET",
      onSuccess: async (response) => {
        if (response?.success && response?.data) {
          console.log(response.data.user);

          try {
            const decryptedUser = await mergeDecrypted(response.data.user);
            console.log(decryptedUser);
            setUserDetails(decryptedUser);
          } catch (err) {
            console.error(
              `Failed to decrypt user id=${response.data.user?.id}:`,
              err,
            );
            setErrorMessage("Failed to decrypt user details");
            setLoading(false);
            return;
          }

          setUserOrders(response.data.orders || []);
        } else {
          setErrorMessage(response?.message || "Failed to load user details");
        }
        setLoading(false);
      },
      onError: (err) => {
        console.error("User details fetch error:", err);
        setErrorMessage(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load user details",
        );
        setLoading(false);
      },
    });
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case "cancelled":
      case "canceled":
        return "#ef4444"; // red
      case "completed":
        return "#10b981"; // green
      case "pending":
        return "#eab308"; // yellow
      case "processing":
        return "#06b6d4"; // cyan/blue
      case "paid":
        return "#3b82f6"; // blue
      case "refunded":
        return "#8b5cf6"; // purple
      default:
        return "#94a3b8"; // gray
    }
  };

  const getPaymentStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case "paid":
        return "#10b981";
      case "pending":
        return "#eab308";
      case "failed":
        return "#ef4444";
      case "refunded":
        return "#8b5cf6";
      default:
        return "#94a3b8";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "0.00";
    return `${parseFloat(amount).toFixed(2)}`;
  };

  const formatPoints = (points) => {
    if (!points) return "0 pts";
    return `${parseFloat(points).toFixed(2)} pts`;
  };

  if (loading) {
    return (
      <div
        style={{
          padding: "24px",
          backgroundColor: "#fff",
          minHeight: "100vh",
          borderRadius: "8px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin size="large" tip="Loading customer details..." />
      </div>
    );
  }

  if (errorMessage && !userDetails) {
    return (
      <div
        style={{
          padding: "24px",
          backgroundColor: "#fff",
          minHeight: "100vh",
          borderRadius: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
          }}
        >
          <Title level={3} style={{ margin: 0, fontWeight: 600 }}>
            Customer Details
          </Title>
          <Button
            onClick={onBack}
            style={{ fontWeight: 500, color: "#475569" }}
          >
            Back
          </Button>
        </div>
        <Alert
          type="error"
          message="Error loading customer details"
          description={errorMessage}
          showIcon
        />
      </div>
    );
  }

  const displayData = userDetails || customer;
  const orderCount = userDetails?.order_count || customer?.order_count || 0;
  const totalRevenue =
    userDetails?.total_orders_value || customer?.total_orders_value || 0;
  const totalPointsSpent =
    userDetails?.total_points_spent || customer?.total_points_spent || 0;
  const averageOrderValue =
    userDetails?.average_order_value || customer?.average_order_value || 0;

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#fff",
        minHeight: "100vh",
        borderRadius: "8px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <Space align="center" size="middle">
          <Title level={3} style={{ margin: 0, fontWeight: 600 }}>
            {displayData?.name || "N/A"}
          </Title>
          <Tag
            style={{
              backgroundColor: displayData?.is_active ? "#10b981" : "#ef4444",
              color: "white",
              borderRadius: "12px",
              border: "none",
              padding: "0 10px",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            {displayData?.is_active ? "Active" : "Inactive"}
          </Tag>
          {displayData?.role && (
            <Tag
              style={{
                backgroundColor: "#3b82f6",
                color: "white",
                borderRadius: "12px",
                border: "none",
                padding: "0 10px",
                fontSize: "12px",
                fontWeight: 500,
              }}
            >
              {displayData.role}
            </Tag>
          )}
        </Space>
        <Button onClick={onBack} style={{ fontWeight: 500, color: "#475569" }}>
          Back
        </Button>
      </div>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={24} md={12} xl={6}>
          <Card
            bordered
            style={{
              borderRadius: 12,
              boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <Text style={{ color: "#64748b", fontSize: "14px" }}>
                  Total Orders
                </Text>
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 28,
                    fontWeight: 700,
                    color: "#0f172a",
                  }}
                >
                  {orderCount}
                </div>
              </div>
              <ShoppingBag size={40} color="#3b82f6" />
            </div>
          </Card>
        </Col>
        <Col span={24} md={12} xl={6}>
          <Card
            bordered
            style={{
              borderRadius: 12,
              boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <Text style={{ color: "#64748b", fontSize: "14px" }}>
                  Total Revenue
                </Text>
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 28,
                    fontWeight: 700,
                    color: "#10b981",
                  }}
                >
                  {formatCurrency(totalRevenue)}
                </div>
              </div>
              <DollarSign size={40} color="#10b981" />
            </div>
          </Card>
        </Col>
        <Col span={24} md={12} xl={6}>
          <Card
            bordered
            style={{
              borderRadius: 12,
              boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <Text style={{ color: "#64748b", fontSize: "14px" }}>
                  Points Spent
                </Text>
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 28,
                    fontWeight: 700,
                    color: "#8b5cf6",
                  }}
                >
                  {formatPoints(totalPointsSpent)}
                </div>
              </div>
              <Award size={40} color="#8b5cf6" />
            </div>
          </Card>
        </Col>
        <Col span={24} md={12} xl={6}>
          <Card
            bordered
            style={{
              borderRadius: 12,
              boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <Text style={{ color: "#64748b", fontSize: "14px" }}>
                  Avg Order Value
                </Text>
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 28,
                    fontWeight: 700,
                    color: "#f59e0b",
                  }}
                >
                  {formatCurrency(averageOrderValue)}
                </div>
              </div>
              <ShoppingBag size={40} color="#f59e0b" />
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={12}>
        {/* Left Column - Orders */}
        <Col span={24} md={16}>
          <Card
            title={
              <span style={{ fontWeight: 600, color: "#334155" }}>
                Orders ({orderCount})
              </span>
            }
            extra={
              <span style={{ fontWeight: 600, color: "#334155" }}>
                Total Revenue - {formatCurrency(totalRevenue)}
              </span>
            }
            style={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
            bodyStyle={{ padding: 0 }}
            headStyle={{ borderBottom: "1px solid #e2e8f0", padding: "0 20px" }}
          >
            <div style={{ height: "600px", overflowY: "scroll" }}>
              {userOrders.length > 0 ? (
                userOrders.map((order, index) => (
                  <div
                    key={order.id}
                    onClick={() => navigate("/sales/orders")}
                    style={{
                      display: "flex",
                      gap: "20px",
                      padding: "20px",
                      cursor: "pointer",
                      borderBottom:
                        index < userOrders.length - 1
                          ? "1px solid #e2e8f0"
                          : "none",
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#334155",
                          marginBottom: "4px",
                        }}
                      >
                        # {order.order_number || order.id}
                      </div>
                      <div
                        style={{
                          color: "#64748b",
                          fontSize: "13px",
                          marginBottom: "8px",
                        }}
                      >
                        {formatDate(order.created_at)}
                      </div>
                      <Tag
                        style={{
                          backgroundColor: getStatusColor(order.status),
                          color: "white",
                          borderRadius: "12px",
                          border: "none",
                          padding: "0 10px",
                          fontSize: "12px",
                        }}
                      >
                        {order.status || "N/A"}
                      </Tag>
                    </div>
                    <div style={{ flex: 1, minWidth: 120 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#334155",
                          marginBottom: "4px",
                        }}
                      >
                        {formatCurrency(order.total_amount)}
                      </div>
                      <div
                        style={{
                          color: "#64748b",
                          fontSize: "13px",
                          marginBottom: "4px",
                        }}
                      >
                        Points Used
                      </div>
                      <div style={{ color: "#64748b", fontSize: "13px" }}>
                        {formatPoints(order.points_used)}
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 100 }}>
                      <div
                        style={{
                          color: "#64748b",
                          fontSize: "13px",
                          marginBottom: "4px",
                        }}
                      >
                        Payment
                      </div>
                      <Tag
                        style={{
                          backgroundColor: getPaymentStatusColor(
                            order.payment_status,
                          ),
                          color: "white",
                          borderRadius: "12px",
                          border: "none",
                          padding: "0 10px",
                          fontSize: "11px",
                        }}
                      >
                        {order.payment_status || "N/A"}
                      </Tag>
                    </div>
                    <div style={{ flex: 2, minWidth: 200 }}>
                      <div
                        style={{
                          color: "#64748b",
                          fontSize: "13px",
                          marginBottom: "4px",
                        }}
                      >
                        Items: {order.items_count || 0}
                      </div>
                      {order.notes && (
                        <div style={{ color: "#64748b", fontSize: "12px" }}>
                          Notes: {order.notes}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <Empty
                  description="No orders found"
                  style={{ padding: "40px" }}
                />
              )}
            </div>
          </Card>
        </Col>

        {/* Right Column - Customer Info & Addresses */}
        <Col span={24} md={8}>
          {/* Customer Info Card */}
          <Card
            title={
              <span style={{ fontWeight: 600, color: "#334155" }}>
                Customer Information
              </span>
            }
            style={{
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              marginBottom: 16,
            }}
            headStyle={{ borderBottom: "1px solid #e2e8f0", padding: "0 20px" }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item
                label={
                  <Space>
                    <User size={14} /> Name
                  </Space>
                }
              >
                {displayData?.name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <Space>
                    <Mail size={14} /> Email
                  </Space>
                }
              >
                {displayData?.email || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <Space>
                    <Phone size={14} /> Phone
                  </Space>
                }
              >
                {displayData?.phone_number || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <Space>
                    <Calendar size={14} /> Customer Since
                  </Space>
                }
              >
                {formatDate(displayData?.created_at)}
              </Descriptions.Item>
              {displayData?.first_order_date && (
                <Descriptions.Item label="First Order">
                  {formatDate(displayData.first_order_date)}
                </Descriptions.Item>
              )}
              {displayData?.last_order_date && (
                <Descriptions.Item label="Last Order">
                  {formatDate(displayData.last_order_date)}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Address Card */}
          {(displayData?.address ||
            displayData?.city ||
            displayData?.state ||
            displayData?.country) && (
            <Card
              title={
                <span style={{ fontWeight: 600, color: "#334155" }}>
                  Address
                </span>
              }
              style={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
              headStyle={{
                borderBottom: "1px solid #e2e8f0",
                padding: "0 20px",
              }}
            >
              <div style={{ padding: "20px" }}>
                <div
                  style={{
                    color: "#334155",
                    fontWeight: 500,
                    marginBottom: "12px",
                  }}
                >
                  {displayData?.name || "Customer"}
                </div>
                <div
                  style={{
                    color: "#64748b",
                    fontSize: "13px",
                    marginBottom: "4px",
                    lineHeight: "1.5",
                  }}
                >
                  {displayData?.address && <div>{displayData.address}</div>}
                  <div>
                    {[
                      displayData?.city,
                      displayData?.state,
                      displayData?.zipcode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                  <div>{displayData?.country || ""}</div>
                </div>
                {displayData?.phone_number && (
                  <div
                    style={{
                      color: "#64748b",
                      fontSize: "13px",
                      marginTop: "8px",
                    }}
                  >
                    Phone - {displayData.phone_number}
                  </div>
                )}
                {displayData?.email && (
                  <div
                    style={{
                      color: "#64748b",
                      fontSize: "13px",
                      marginTop: "4px",
                    }}
                  >
                    Email - {displayData.email}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Actions */}
          {/* <Space style={{ marginTop: 16 }} direction="vertical" size="middle">
                        <Button type="text" icon={<MapPin size={16} />} style={{ color: '#475569', padding: 0 }}>
                            Add New Address
                        </Button>
                        <Button
                            type="text"
                            icon={<XCircle size={16} />}
                            style={{ color: "#ef4444", padding: 0 }}
                            onClick={() => setOpen(true)}
                        >
                            Delete Account
                        </Button>
                    </Space> */}

          <Modal
            open={open}
            onCancel={() => setOpen(false)}
            onOk={() => {
              console.log("Deleted!");
              setOpen(false);
            }}
            okText="Yes, Delete"
            cancelText="Cancel"
            title="Delete Customer Account"
            okButtonProps={{ danger: true }}
          >
            <p>Are you sure you want to delete this customer account?</p>
            <p style={{ color: "#ef4444", fontSize: "12px" }}>
              This action cannot be undone.
            </p>
          </Modal>
        </Col>
      </Row>
    </div>
  );
};

export default CustomerDetails;
